/**
 * Tying the bank to the books — and knowing every rupee that left was approved.
 *
 * Top to bottom, the order work is done in:
 *
 * 1. Does the account agree with the ledger?
 * 2. Debits the bank control has flagged — no approval, a different amount, or
 *    paid twice. These are the Finance Head's, and a Book Keeper sees them as
 *    "with the Finance Head".
 * 3. Proposals. One against a payment request shows the evidence and the
 *    approval behind it, and settling it posts it — the Book Keeper's step when
 *    the amount agrees, the Finance Head's when it does not.
 * 4. What is matched, and a way to reverse a settlement.
 * 5. The control's settings, which an Admin changes.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface Member {
  kind: string
  ref_id: string
  label: string
  amount_paise: number
  is_partial: boolean
}

interface Check {
  label: string
  ok: boolean
  detail: string | null
}

interface Match {
  id: string
  status: string
  tier: string
  confidence: number
  difference_paise: number
  explanation: string
  reason: string | null
  decided_at: string | null
  members: Member[]
  auto_eligible: boolean
  evidence: string[]
  settles_request: boolean
  checks: Check[]
}

interface Summary {
  bank_account_id: string
  as_at: string
  per_statement_paise: number
  per_books_paise: number
  difference_paise: number
  reconciled: boolean
  unmatched_count: number
  unmatched_paise: number
}

interface BankAccount {
  id: string
  name: string
}

interface ControlSettings {
  mode: 'off' | 'warn' | 'enforce'
  control_from: string | null
  small_debit_allowance_paise: number
  payment_tolerance_paise: number
  request_grace_days: number
  auto_confirm_settlements: boolean
  active: boolean
  enforcing: boolean
  upi_standing_approver_id: string | null
  upi_standing_approver_name: string | null
  upi_standing_approver_choices: { id: string; name: string; role: string }[]
}

interface ControlLine {
  id: string
  bank_account_name: string | null
  txn_date: string
  amount_paise: number
  narration: string
  reference: string | null
  posting_state: string
  match_state: string
  state: string
  why: string
  duplicate_of: { id: string; txn_date: string; amount_paise: number; narration: string } | null
  payment_request_id: string | null
  payment_request_reference: string | null
  resolutions: {
    action: string
    reason: string
    decided_by: string | null
    decided_at: string
    payment_request_reference: string | null
  }[]
}

interface Controls {
  settings: ControlSettings
  lines: ControlLine[]
  makers: { id: string; name: string; role: string }[]
}

interface Option {
  id: string
  name: string
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  categories: Option[]
  vendors: Option[]
}

const STATE_LABELS: Record<string, string> = {
  awaiting_request: 'awaiting request',
  unapproved: 'unapproved',
  mismatch: 'amount mismatch',
  duplicate_suspect: 'paid twice?',
  resolved: 'resolved',
}

const TREATMENTS: [string, string][] = [
  ['partial', 'Part payment — the rest is still owed'],
  ['short_close', 'Short close — the rest will never be paid'],
  ['excess_to_suspense', 'Excess to suspense — nobody approved it'],
  ['excess_approved', 'Excess approved — with its supplementary request'],
]

function rupees(paise: number) {
  return (paise / 100).toFixed(2)
}

function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  return clean ? Math.round(Number(clean) * 100) : 0
}

export default function Reconciliation() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [account, setAccount] = useState<string>('')
  const refresh = () => {
    for (const key of ['matches', 'recon-summary', 'bank-controls', 'exceptions', 'payment-requests']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const banks = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
    enabled: can('org.bank_account.view'),
  })
  const matches = useQuery({
    queryKey: ['matches'],
    queryFn: () => api.get<Match[]>('/reconciliation/matches'),
  })
  const chosen = account || banks.data?.[0]?.id || ''
  const summary = useQuery({
    queryKey: ['recon-summary', chosen],
    queryFn: () => api.get<Summary>(`/reconciliation/summary/${chosen}`),
    enabled: !!chosen && can('recon.statement.view'),
  })

  const run = useMutation({
    mutationFn: () =>
      api.post<Match[]>(`/reconciliation/propose${chosen ? `?bank_account_id=${chosen}` : ''}`),
    onSuccess: refresh,
  })
  const confirm = useMutation({
    mutationFn: (id: string) => api.post<Match>(`/reconciliation/matches/${id}/confirm`),
    onSuccess: refresh,
  })
  const settle = useMutation({
    mutationFn: (id: string) => api.post<Match>(`/reconciliation/matches/${id}/settle`, {}),
    onSuccess: refresh,
  })
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<Match>(`/reconciliation/matches/${id}/reject`, { reason }),
    onSuccess: refresh,
  })
  const dissolve = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<Match>(`/reconciliation/matches/${id}/dissolve`, { reason }),
    onSuccess: refresh,
  })
  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<Match>(`/reconciliation/settlements/${id}/reverse`, { reason }),
    onSuccess: refresh,
  })
  const [differenceFor, setDifferenceFor] = useState<string | null>(null)

  if (matches.isLoading) return <Loading what="Loading reconciliation" />
  const rows = matches.data ?? []
  const proposed = rows.filter((m) => m.status === 'proposed')
  const confirmed = rows.filter((m) => m.status === 'confirmed')

  const askAndReject = (id: string) => {
    const reason = window.prompt(
      'Why is this wrong? The combination will never be proposed again, so the reason is what makes that decision reviewable later.',
    )
    if (reason && reason.trim()) reject.mutate({ id, reason })
  }
  const askAndDissolve = (id: string) => {
    const reason = window.prompt('Why are you taking this match apart?')
    if (reason && reason.trim()) dissolve.mutate({ id, reason })
  }
  const askAndReverse = (id: string) => {
    const reason = window.prompt(
      'Why is this settlement being reversed? Its entry is reversed, and the bank line and the request go back to open.',
    )
    if (reason && reason.trim()) reverse.mutate({ id, reason })
  }

  return (
    <>
      <ErrorNote
        error={
          matches.error ??
          run.error ??
          confirm.error ??
          settle.error ??
          reject.error ??
          dissolve.error ??
          reverse.error
        }
      />

      <Card
        title="Does the bank agree with the books?"
        actions={
          <>
            {banks.data && banks.data.length > 1 && (
              <select value={chosen} onChange={(e) => setAccount(e.target.value)}>
                {banks.data.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}{' '}
            {can('recon.match.create') && (
              <button className="small primary" disabled={run.isPending} onClick={() => run.mutate()}>
                Look for matches
              </button>
            )}
          </>
        }
      >
        {summary.data ? (
          <>
            <div className="row">
              <div>
                <div className="caption">Per the statement</div>
                <div className="num">{formatInr(summary.data.per_statement_paise)}</div>
              </div>
              <div>
                <div className="caption">Per the books</div>
                <div className="num">{formatInr(summary.data.per_books_paise)}</div>
              </div>
              <div>
                <div className="caption">Difference</div>
                <div className="num">{formatInr(summary.data.difference_paise)}</div>
              </div>
              <div>
                <div className="caption">Unmatched lines</div>
                <div className="num">{summary.data.unmatched_count}</div>
              </div>
            </div>
            {summary.data.reconciled ? (
              <Note kind="ok">This account agrees with the ledger as at {summary.data.as_at}.</Note>
            ) : (
              <Note kind="warn">
                The two disagree by {formatInr(Math.abs(summary.data.difference_paise))}. The{' '}
                {summary.data.unmatched_count} unmatched lines are the work — either something
                happened at the bank that the books never saw, or something was recorded that never
                reached the bank.
              </Note>
            )}
          </>
        ) : (
          <div className="empty">Choose an account.</div>
        )}
      </Card>

      {can('recon.statement.view') && <BankControls onDone={refresh} />}

      <Card title="Waiting on a person" hint={`${proposed.length}`} tight>
        {proposed.length === 0 ? (
          <div className="empty">Nothing proposed.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>What was found</th>
                <th>Rows</th>
                <th className="num">Difference</th>
                <th>How</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {proposed.map((match) => (
                <tr key={match.id}>
                  <td>
                    {match.explanation}
                    {match.evidence.length > 0 && (
                      <ul className="faint" style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12 }}>
                        {match.evidence.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                    )}
                    {match.checks.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12 }}>
                        {match.checks.map((c) => (
                          <span key={c.label} style={{ marginRight: 10, whiteSpace: 'nowrap' }}>
                            <span style={{ color: c.ok ? 'var(--ok)' : 'var(--danger)' }}>
                              {c.ok ? '✓' : '✗'}
                            </span>{' '}
                            {c.label}
                            {c.detail ? <span className="faint"> ({c.detail})</span> : null}
                          </span>
                        ))}
                      </div>
                    )}
                    {!match.auto_eligible && !match.settles_request && (
                      <div className="faint">
                        This kind of match never confirms itself, however well it scores.
                      </div>
                    )}
                    {differenceFor === match.id && (
                      <DifferenceForm
                        match={match}
                        onCancel={() => setDifferenceFor(null)}
                        onDone={() => {
                          setDifferenceFor(null)
                          refresh()
                        }}
                      />
                    )}
                  </td>
                  <td className="muted">
                    {match.members.map((m) => (
                      <div key={m.ref_id}>
                        {m.label} {formatInr(m.amount_paise)}
                        {m.is_partial && <span className="faint"> (part)</span>}
                      </div>
                    ))}
                  </td>
                  <td className="num">
                    {match.difference_paise ? (
                      formatInr(Math.abs(match.difference_paise))
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>
                    <Pill value={match.tier} />
                    <div className="faint">{match.confidence}%</div>
                  </td>
                  <td className="num">
                    {match.settles_request ? (
                      <>
                        {can('recon.statement.reconcile') && match.tier !== 'amount_mismatch' && (
                          <button
                            className="small primary"
                            disabled={settle.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Settle this payment request against the bank line and post it to the books?',
                                )
                              )
                                settle.mutate(match.id)
                            }}
                          >
                            Settle &amp; post
                          </button>
                        )}{' '}
                        {can('compliance.exception.approve') && (
                          <button className="small" onClick={() => setDifferenceFor(match.id)}>
                            Settle with a difference
                          </button>
                        )}
                        {!can('recon.statement.reconcile') &&
                          !can('compliance.exception.approve') && (
                            <span className="faint">with the Book Keeper</span>
                          )}
                        {can('recon.statement.reconcile') && match.tier === 'amount_mismatch' && (
                          <span className="faint">with the Finance Head</span>
                        )}
                      </>
                    ) : can('recon.match.approve') ? (
                      <button
                        className="small primary"
                        disabled={confirm.isPending}
                        onClick={() => confirm.mutate(match.id)}
                      >
                        Confirm
                      </button>
                    ) : (
                      <span className="faint">with a checker</span>
                    )}{' '}
                    {can('recon.match.approve') && (
                      <button className="small" onClick={() => askAndReject(match.id)}>
                        Reject
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Matched" hint={`${confirmed.length}`} tight>
        {confirmed.length === 0 ? (
          <div className="empty">Nothing matched yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>What it is</th>
                <th>Rows</th>
                <th>How</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {confirmed.map((match) => (
                <tr key={match.id}>
                  <td>
                    {match.explanation}
                    {match.reason && <div className="faint">{match.reason}</div>}
                  </td>
                  <td className="muted">
                    {match.members.map((m) => (
                      <div key={m.ref_id}>{m.label}</div>
                    ))}
                  </td>
                  <td>
                    <Pill value={match.tier} />
                  </td>
                  <td className="num">
                    {match.settles_request
                      ? can('compliance.exception.approve') && (
                          <button className="small danger" onClick={() => askAndReverse(match.id)}>
                            Reverse settlement
                          </button>
                        )
                      : can('recon.match.approve') && (
                          <button className="small" onClick={() => askAndDissolve(match.id)}>
                            Take apart
                          </button>
                        )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {can('recon.statement.view') && <ParkedMoney onDone={refresh} />}

      {can('recon.statement.view') && <ControlSettingsCard onDone={refresh} />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Settling with a difference (Finance Head)
// ---------------------------------------------------------------------------

function DifferenceForm({
  match,
  onCancel,
  onDone,
}: {
  match: Match
  onCancel: () => void
  onDone: () => void
}) {
  const bankLess = match.difference_paise > 0
  const [treatment, setTreatment] = useState(bankLess ? 'partial' : 'excess_to_suspense')
  const [reason, setReason] = useState('')
  const [supplementary, setSupplementary] = useState('')
  const [laterDate, setLaterDate] = useState(false)
  const request = match.members.find((m) => m.kind === 'payment_request')

  const save = useMutation({
    mutationFn: () =>
      api.post(`/reconciliation/matches/${match.id}/settle-with-difference`, {
        treatment,
        reason,
        supplementary_request_id: treatment === 'excess_approved' ? supplementary || null : null,
        post_in_open_period: laterDate,
      }),
    onSuccess: onDone,
  })
  const raiseSupplement = useMutation({
    mutationFn: () =>
      api.post<{ reference: string }>(`/spend/payment-requests/${request?.ref_id}/supplementary`, {
        amount_paise: Math.abs(match.difference_paise),
        reason: reason || 'the bank paid more than was approved',
      }),
  })

  const choices = TREATMENTS.filter(([value]) =>
    bankLess ? value === 'partial' || value === 'short_close' : value.startsWith('excess'),
  )

  return (
    <form
      style={{ marginTop: 8 }}
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <ErrorNote error={save.error ?? raiseSupplement.error} />
      <div className="row">
        <Field label="Treatment">
          <select value={treatment} onChange={(e) => setTreatment(e.target.value)}>
            {choices.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Why" caption="kept with the settlement">
          <input value={reason} onChange={(e) => setReason(e.target.value)} required />
        </Field>
      </div>
      <label style={{ display: 'block', margin: '4px 0', fontSize: 13 }}>
        <input type="checkbox" checked={laterDate} onChange={(e) => setLaterDate(e.target.checked)} />{' '}
        If the bank line's month is closed, post on the first open day (the bank date is kept)
      </label>
      {treatment === 'excess_approved' && (
        <Note kind="info">
          The excess needs its own approval. Raise a supplementary request for{' '}
          {formatInr(Math.abs(match.difference_paise))} — it goes to the person who raised the
          original to submit, through the normal route — then, once approved, paste its id here.{' '}
          <button
            type="button"
            className="small"
            disabled={raiseSupplement.isPending || !request}
            onClick={() => raiseSupplement.mutate()}
          >
            Raise supplementary request
          </button>
          {raiseSupplement.data && <b> Raised {raiseSupplement.data.reference}.</b>}
          <div style={{ marginTop: 6 }}>
            <input
              placeholder="supplementary request id"
              value={supplementary}
              onChange={(e) => setSupplementary(e.target.value)}
            />
          </div>
        </Note>
      )}
      <div className="row">
        <div className="shrink">
          <button className="primary small" type="submit" disabled={save.isPending}>
            Settle &amp; post
          </button>{' '}
          <button type="button" className="small" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// The bank debit control
// ---------------------------------------------------------------------------

function BankControls({ onDone }: { onDone: () => void }) {
  const { can } = useAuth()
  const [showResolved, setShowResolved] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const controls = useQuery({
    queryKey: ['bank-controls', showResolved],
    queryFn: () =>
      api.get<Controls>(`/reconciliation/controls?include_resolved=${showResolved}`),
  })
  const decider = can('compliance.exception.approve')

  if (controls.isLoading) return <Loading what="Checking bank debits" />
  if (controls.error) return <ErrorNote error={controls.error} />
  const data = controls.data!
  const s = data.settings

  return (
    <Card
      title="Money that left without an approval"
      hint={
        !s.active
          ? 'control not switched on'
          : `${data.lines.length} flagged · ${s.mode} from ${s.control_from}`
      }
      actions={
        <label style={{ fontSize: 12 }}>
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />{' '}
          show decided
        </label>
      }
      tight
    >
      {!s.active ? (
        <div className="empty">
          The bank debit control is not on yet. Once an Admin sets the date it starts from, every
          debit after that date is checked for an approved payment request.
        </div>
      ) : data.lines.length === 0 ? (
        <div className="empty">Every debit since {s.control_from} is covered.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Bank line</th>
              <th className="num">Amount</th>
              <th>State</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.id}>
                <td className="muted date">{line.txn_date}</td>
                <td>
                  {line.narration}
                  <div className="faint" style={{ fontSize: 12 }}>
                    {line.bank_account_name}
                    {line.reference ? ` · ${line.reference}` : ''} — {line.why}
                  </div>
                  {line.duplicate_of && (
                    <div className="faint" style={{ fontSize: 12 }}>
                      Repeats {formatInr(line.duplicate_of.amount_paise)} on {line.duplicate_of.txn_date}:{' '}
                      {line.duplicate_of.narration}
                    </div>
                  )}
                  {line.resolutions.map((r) => (
                    <div key={r.decided_at} className="faint" style={{ fontSize: 12 }}>
                      {r.action.replace(/_/g, ' ')} by {r.decided_by} on {r.decided_at.slice(0, 10)}
                      {r.payment_request_reference ? ` (${r.payment_request_reference})` : ''}: {r.reason}
                    </div>
                  ))}
                  {open === line.id && (
                    <ResolveForm
                      line={line}
                      makers={data.makers}
                      onCancel={() => setOpen(null)}
                      onDone={() => {
                        setOpen(null)
                        onDone()
                      }}
                    />
                  )}
                </td>
                <td className="num">{formatInr(line.amount_paise)}</td>
                <td>
                  <span className={`pill ${line.state}`}>{STATE_LABELS[line.state] ?? line.state}</span>
                </td>
                <td className="num">
                  {line.state === 'resolved' ? null : line.state === 'mismatch' ? (
                    <span className="faint">settle it below</span>
                  ) : decider ? (
                    open !== line.id && (
                      <button className="small primary" onClick={() => setOpen(line.id)}>
                        Decide
                      </button>
                    )
                  ) : (
                    <span className="faint">with the Finance Head</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {s.active && (
        <Note kind="info">
          {s.enforcing
            ? 'Enforced: a flagged debit cannot be posted from bank statements until the Finance Head decides it.'
            : 'Warn only: a flagged debit can still be posted from bank statements, and stays flagged here.'}{' '}
          Debits up to {formatInr(s.small_debit_allowance_paise)} and categories marked as not
          needing a payment request are not flagged.
        </Note>
      )}
    </Card>
  )
}

function ResolveForm({
  line,
  makers,
  onCancel,
  onDone,
}: {
  line: ControlLine
  makers: Controls['makers']
  onCancel: () => void
  onDone: () => void
}) {
  const duplicate = line.state === 'duplicate_suspect'
  const [action, setAction] = useState(duplicate ? 'recovery' : 'retrospective')
  const [form, setForm] = useState({
    reason: '',
    maker_user_id: '',
    department_id: '',
    location_id: '',
    category_id: '',
    party_id: '',
  })
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
    enabled: !duplicate,
  })
  const save = useMutation({
    mutationFn: () =>
      duplicate
        ? api.post(`/reconciliation/controls/${line.id}/duplicate`, { action, reason: form.reason })
        : api.post(`/reconciliation/controls/${line.id}/unapproved`, {
            action,
            reason: form.reason,
            maker_user_id: form.maker_user_id || null,
            department_id: form.department_id || null,
            location_id: form.location_id || null,
            category_id: form.category_id || null,
            party_id: form.party_id || null,
          }),
    onSuccess: onDone,
  })
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch })
  const choices: [string, string][] = duplicate
    ? [
        ['recovery', 'Paid twice — recover it from the vendor (held as an advance)'],
        ['not_duplicate', 'Not a duplicate — a separate payment'],
      ]
    : [
        ['retrospective', 'Approve after payment — raise a request for the normal route'],
        ['unauthorised', 'Unauthorised — park it in suspense until recovered or written off'],
        ['request_documents', 'Ask for the documents first'],
      ]

  return (
    <form
      style={{ marginTop: 8 }}
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <ErrorNote error={save.error} />
      <div className="row">
        <Field label="Decision">
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            {choices.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Why" caption="kept on the line's history">
          <input value={form.reason} onChange={(e) => set({ reason: e.target.value })} required />
        </Field>
      </div>
      {action === 'retrospective' && (
        <div className="row">
          <Field label="Raised for" caption="they submit it; not you">
            <select
              value={form.maker_user_id}
              onChange={(e) => set({ maker_user_id: e.target.value })}
              required
            >
              <option value="">Choose…</option>
              {makers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.role.replace('_', ' ')})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Department">
            <select
              value={form.department_id}
              onChange={(e) => set({ department_id: e.target.value })}
              required
            >
              <option value="">Choose…</option>
              {(options.data?.departments ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Clinic">
            <select
              value={form.location_id}
              onChange={(e) => set({ location_id: e.target.value })}
              required
            >
              <option value="">Choose…</option>
              {(options.data?.locations ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Category">
            <SearchSelect
              aria-label="Category"
              value={form.category_id}
              onChange={(v) => set({ category_id: v })}
              required
              placeholder="Choose…"
              options={(options.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
        </div>
      )}
      {(action === 'retrospective' || action === 'unauthorised') && (
        <div className="row">
          <Field label="Paid to" caption="optional">
            <select value={form.party_id} onChange={(e) => set({ party_id: e.target.value })}>
              <option value="">Unknown</option>
              {(options.data?.vendors ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}
      <div className="row">
        <div className="shrink">
          <button className="primary small" type="submit" disabled={save.isPending}>
            Record decision
          </button>{' '}
          <button type="button" className="small" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  )
}

function ControlSettingsCard({ onDone }: { onDone: () => void }) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const current = useQuery({
    queryKey: ['control-settings'],
    queryFn: () => api.get<ControlSettings>('/reconciliation/control-settings'),
  })
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    mode: 'warn',
    control_from: '',
    allowance: '',
    tolerance: '',
    grace: 3,
    auto: false,
    upi: '',
  })
  const save = useMutation({
    mutationFn: () =>
      api.put<ControlSettings>('/reconciliation/control-settings', {
        mode: form.mode,
        control_from: form.control_from || null,
        small_debit_allowance_paise: toPaise(form.allowance),
        payment_tolerance_paise: toPaise(form.tolerance),
        request_grace_days: form.grace,
        auto_confirm_settlements: form.auto,
        upi_standing_approver_id: form.upi || null,
      }),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: ['control-settings'] })
      onDone()
    },
  })
  if (!current.data) return null
  const s = current.data
  const configurable = can('org.profile.configure')

  return (
    <Card
      title="Bank debit control"
      hint={s.active ? `${s.mode}, from ${s.control_from}` : 'off'}
      actions={
        configurable &&
        !editing && (
          <button
            className="small"
            onClick={() => {
              setForm({
                mode: s.mode,
                control_from: s.control_from ?? '',
                allowance: rupees(s.small_debit_allowance_paise),
                tolerance: rupees(s.payment_tolerance_paise),
                grace: s.request_grace_days,
                auto: s.auto_confirm_settlements,
                upi: s.upi_standing_approver_id ?? '',
              })
              setEditing(true)
            }}
          >
            Change
          </button>
        )
      }
    >
      {!editing ? (
        <div className="row">
          <div>
            <div className="caption">Mode</div>
            {s.mode}
          </div>
          <div>
            <div className="caption">Starts from</div>
            {s.control_from ?? <span className="faint">not set</span>}
          </div>
          <div>
            <div className="caption">Small debits not checked</div>
            up to {formatInr(s.small_debit_allowance_paise)}
          </div>
          <div>
            <div className="caption">Bank charge tolerance</div>
            {formatInr(s.payment_tolerance_paise)}
          </div>
          <div>
            <div className="caption">Grace days</div>
            {s.request_grace_days}
          </div>
          <div>
            <div className="caption">Settle on our reference automatically</div>
            {s.auto_confirm_settlements ? 'yes' : 'no'}
          </div>
          <div>
            <div className="caption">UPI debits approved in advance by</div>
            {s.upi_standing_approver_name ?? <span className="faint">nobody — checked like any debit</span>}
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <ErrorNote error={save.error} />
          <div className="row">
            <Field label="Mode">
              <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="off">Off</option>
                <option value="warn">Warn — flag, still allow posting</option>
                <option value="enforce">Enforce — flag and refuse posting</option>
              </select>
            </Field>
            <Field label="Starts from" caption="nothing earlier is judged">
              <input
                type="date"
                value={form.control_from}
                onChange={(e) => setForm({ ...form, control_from: e.target.value })}
              />
            </Field>
            <Field label="Small debit allowance (₹)">
              <input
                className="num"
                value={form.allowance}
                onChange={(e) => setForm({ ...form, allowance: e.target.value })}
              />
            </Field>
            <Field label="Bank charge tolerance (₹)">
              <input
                className="num"
                value={form.tolerance}
                onChange={(e) => setForm({ ...form, tolerance: e.target.value })}
              />
            </Field>
            <Field label="Grace days">
              <input
                type="number"
                min={0}
                max={30}
                value={form.grace}
                onChange={(e) => setForm({ ...form, grace: Number(e.target.value) })}
              />
            </Field>
          </div>
          <label style={{ display: 'block', margin: '6px 0' }}>
            <input
              type="checkbox"
              checked={form.auto}
              onChange={(e) => setForm({ ...form, auto: e.target.checked })}
            />{' '}
            Settle automatically when the bank line carries our request number or recorded UTR and
            the amount is exact (this posts without a person)
          </label>
          <Field
            label="UPI debits approved in advance by"
            caption="every UPI debit goes through on this person's standing approval: the Book Keeper sets the category and department and posts it, with no payment request or question. A possible second payment is still held."
          >
            <select value={form.upi} onChange={(e) => setForm({ ...form, upi: e.target.value })}>
              <option value="">Nobody — check UPI debits like any other</option>
              {s.upi_standing_approver_choices.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.role === 'finance_head' ? 'Finance Head' : 'Admin'})
                </option>
              ))}
            </select>
          </Field>
          <div className="row">
            <div className="shrink">
              <button className="primary" type="submit" disabled={save.isPending}>
                Save
              </button>{' '}
              <button type="button" className="small" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Money a flagged payment parked: refunds and write-offs
// ---------------------------------------------------------------------------

interface Parked {
  balances: { source: string; party_id: string | null; party_name: string | null; amount_paise: number }[]
  credit_lines: {
    id: string
    bank_account_name: string | null
    txn_date: string
    amount_paise: number
    narration: string
    reference: string | null
  }[]
  write_offs: {
    id: string
    reference: string
    source: string
    party_id: string | null
    party_name: string | null
    amount_paise: number
    reason: string
    status: string
    approval_state: string
    created_by_name: string | null
  }[]
}

const SOURCE_LABELS: Record<string, string> = {
  suspense: 'Suspense (paid without approval)',
  supplier_advance: 'Advance to the vendor',
}

function ParkedMoney({ onDone }: { onDone: () => void }) {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const parked = useQuery({
    queryKey: ['parked-money'],
    queryFn: () => api.get<Parked>('/reconciliation/parked'),
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['parked-money'] })
    onDone()
  }
  const [refundFor, setRefundFor] = useState<Parked['balances'][number] | null>(null)
  const [refund, setRefund] = useState({ line: '', reason: '' })
  const recordRefund = useMutation({
    mutationFn: () =>
      api.post(
        `/reconciliation/refunds/${refundFor?.source === 'suspense' ? 'suspense' : 'advance'}`,
        { bank_transaction_id: refund.line, party_id: refundFor?.party_id ?? null, reason: refund.reason },
      ),
    onSuccess: () => {
      setRefundFor(null)
      setRefund({ line: '', reason: '' })
      refresh()
    },
  })
  const proposeWriteOff = useMutation({
    mutationFn: (b: Parked['balances'][number]) => {
      const reason = window.prompt(
        `Why will ${formatInr(b.amount_paise)} not be recovered? The write-off goes to someone else for approval.`,
      )
      if (!reason || !reason.trim()) return Promise.resolve(null)
      return api.post('/reconciliation/write-offs', {
        source: b.source,
        party_id: b.party_id,
        amount_paise: b.amount_paise,
        reason,
      })
    },
    onSuccess: refresh,
  })
  const submitWriteOff = useMutation({
    mutationFn: (id: string) => api.post(`/reconciliation/write-offs/${id}/submit`),
    onSuccess: refresh,
  })
  const postWriteOff = useMutation({
    mutationFn: (id: string) => api.post(`/reconciliation/write-offs/${id}/post`),
    onSuccess: refresh,
  })

  const data = parked.data
  if (!data || (data.balances.length === 0 && data.write_offs.length === 0)) return null
  const canRefund = (source: string) =>
    source === 'suspense' ? can('compliance.exception.approve') : can('recon.statement.reconcile')

  return (
    <Card title="Money parked by a payment" hint="comes back, or is written off" tight>
      <ErrorNote
        error={parked.error ?? recordRefund.error ?? proposeWriteOff.error ?? submitWriteOff.error ?? postWriteOff.error}
      />
      {data.balances.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Where</th>
              <th>Who</th>
              <th className="num">Amount</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.balances.map((b) => (
              <tr key={`${b.source}-${b.party_id}`}>
                <td>{SOURCE_LABELS[b.source] ?? b.source}</td>
                <td className="muted">{b.party_name ?? 'nobody named'}</td>
                <td className="num">{formatInr(b.amount_paise)}</td>
                <td className="num">
                  {canRefund(b.source) && data.credit_lines.length > 0 && (
                    <button className="small" onClick={() => setRefundFor(b)}>
                      It came back
                    </button>
                  )}{' '}
                  {can('gl.journal.create') && (
                    <button className="small" onClick={() => proposeWriteOff.mutate(b)}>
                      Propose write-off
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {refundFor && (
        <form
          style={{ padding: 12 }}
          onSubmit={(e) => {
            e.preventDefault()
            recordRefund.mutate()
          }}
        >
          <div className="row">
            <Field label="The credit on the statement">
              <select value={refund.line} onChange={(e) => setRefund({ ...refund, line: e.target.value })} required>
                <option value="">Choose…</option>
                {data.credit_lines.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.txn_date} {formatInr(c.amount_paise)} — {c.narration.slice(0, 60)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="What came back">
              <input value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} required />
            </Field>
            <div className="shrink">
              <button className="primary small" type="submit" disabled={recordRefund.isPending}>
                Record refund
              </button>{' '}
              <button type="button" className="small" onClick={() => setRefundFor(null)}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
      {data.write_offs.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Write-off</th>
              <th>Reason</th>
              <th className="num">Amount</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.write_offs.map((w) => (
              <tr key={w.id}>
                <td className="mono">
                  {w.reference}
                  <div className="faint">
                    {SOURCE_LABELS[w.source] ?? w.source}
                    {w.party_name ? ` · ${w.party_name}` : ''}
                  </div>
                </td>
                <td className="muted">{w.reason}</td>
                <td className="num">{formatInr(w.amount_paise)}</td>
                <td>
                  <Pill value={w.status} />
                  {w.status === 'submitted' && (
                    <div className="faint" style={{ fontSize: 12 }}>
                      {w.approval_state} — decided from Approvals
                    </div>
                  )}
                </td>
                <td className="num">
                  {w.status === 'draft' && can('gl.journal.submit') && (
                    <button className="small" onClick={() => submitWriteOff.mutate(w.id)}>
                      Submit
                    </button>
                  )}
                  {w.status === 'approved' && can('gl.journal.create') && (
                    <button className="small primary" onClick={() => postWriteOff.mutate(w.id)}>
                      Post
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Note kind="info">
        Suspense from a payment nobody approved has to be recovered or written off before its month
        closes; an advance can wait for the vendor's next bill.
      </Note>
    </Card>
  )
}
